import { api, ApiError } from "../api-client.js";
import { getCurrentUser } from "../session.js";

interface AdminUser {
  id: number;
  username: string;
  jatem_user_id: string;
  role: "ADMIN" | "USER";
  status: "ACTIVE" | "SUSPENDED";
  created_at: string;
}

interface CreatedUser {
  id: number;
  username: string;
  password: string;
  role: "ADMIN" | "USER";
  jatem_user_id: string;
  secret: string;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString("ko-KR");
}

// 사용자 요청: admin이 전체 계정 목록을 보고, 새 일반 사용자 계정을 직접 만들고(매번 요청받는 대신),
// 필요하면 접속을 정지/재개하거나 아예 삭제할 수 있어야 한다.
// - 정지(SUSPENDED): 되돌릴 수 있는 차단. 지금 로그인돼 있어도 즉시 세션을 끊는다.
// - 삭제: 로그인 자격증명 자체를 없앤다(되돌릴 수 없음) — 거래 이력은 남는다(백엔드 참고).
export async function renderUsers(container: HTMLElement): Promise<void> {
  container.innerHTML = `<h2 class="page-title">사용자 관리</h2>`;

  const revealSlot = document.createElement("div");
  const toolbarSlot = document.createElement("div");
  const tableCard = document.createElement("div");
  tableCard.className = "card section-gap";
  container.append(revealSlot, toolbarSlot, tableCard);

  function renderToolbar(): void {
    const bar = document.createElement("div");
    bar.className = "card";
    bar.innerHTML = `
      <div class="stat-label">새 일반 사용자 계정 추가</div>
      <p style="color:var(--text-dim);font-size:14px">
        아이디(a_로 시작)·비밀번호·연동정보를 자동 생성합니다. 노트북 배포 때마다 이 버튼으로 만들면 됩니다.
      </p>
      <button class="btn-primary" id="create-user-btn" style="width:auto;padding:8px 20px;margin-top:8px">
        새 사용자 추가
      </button>
    `;
    const btn = bar.querySelector<HTMLButtonElement>("#create-user-btn")!;
    btn.addEventListener("click", async () => {
      btn.disabled = true;
      btn.textContent = "생성 중...";
      try {
        const created = await api.post<CreatedUser>("/api/admin/users", {});
        renderReveal(created);
        await load();
      } catch {
        alert("계정 생성 중 오류가 발생했습니다.");
      } finally {
        btn.disabled = false;
        btn.textContent = "새 사용자 추가";
      }
    });
    toolbarSlot.replaceChildren(bar);
  }

  function renderReveal(created: CreatedUser): void {
    const card = document.createElement("div");
    card.className = "card section-gap";
    card.innerHTML = `
      <div class="stat-label">계정이 생성되었습니다</div>
      <p style="color:var(--text-dim);font-size:14px">
        <strong style="color:var(--gold)">이 화면을 벗어나면 비밀번호·연동비밀키를 다시 확인할 수 없습니다.</strong>
        지금 바로 복사해 전달하거나 노트북의 개인정보.ini에 반영하세요.
      </p>
      <div class="kv-list" style="margin-top:8px">
        <div class="kv-row"><span class="kv-label">아이디</span><span class="secret-box">${created.username}</span></div>
        <div class="kv-row"><span class="kv-label">비밀번호</span><span class="secret-box">${created.password}</span></div>
        <div class="kv-row"><span class="kv-label">JaTem 사용자ID</span><span class="secret-box">${created.jatem_user_id}</span></div>
        <div class="kv-row"><span class="kv-label">연동비밀키</span><span class="secret-box">${created.secret}</span></div>
      </div>
      <button class="btn-secondary" id="reveal-dismiss" style="width:auto;padding:6px 16px;margin-top:12px">닫기</button>
    `;
    card.querySelector<HTMLButtonElement>("#reveal-dismiss")!.addEventListener("click", () => {
      revealSlot.replaceChildren();
    });
    revealSlot.replaceChildren(card);
  }

  async function load(): Promise<void> {
    renderToolbar();

    let users: AdminUser[];
    try {
      const result = await api.get<{ users: AdminUser[] }>("/api/admin/users");
      users = result.users;
    } catch {
      tableCard.innerHTML = `<p style="color:var(--text-dim);font-size:14px">사용자 목록을 불러오지 못했습니다.</p>`;
      return;
    }

    const myUserId = getCurrentUser()?.jatemUserId;

    const scrollWrap = document.createElement("div");
    scrollWrap.className = "table-scroll";
    const table = document.createElement("table");
    table.innerHTML = `
      <thead>
        <tr><th>아이디</th><th>JaTem 사용자ID</th><th>역할</th><th>상태</th><th>가입일</th><th>작업</th></tr>
      </thead>
      <tbody></tbody>
    `;
    const tbody = table.querySelector("tbody")!;

    for (const u of users) {
      const tr = document.createElement("tr");
      const isSelf = u.jatem_user_id === myUserId;
      const suspended = u.status === "SUSPENDED";

      tr.innerHTML = `
        <td>${u.username}${isSelf ? ' <span style="color:var(--text-dim);font-size:12px">(나)</span>' : ""}</td>
        <td>${u.jatem_user_id}</td>
        <td>${u.role}</td>
        <td class="${suspended ? "status-danger" : ""}">${suspended ? "정지됨" : "정상"}</td>
        <td>${fmtDate(u.created_at)}</td>
        <td></td>
      `;

      const actionCell = tr.querySelector("td:last-child")!;
      if (!isSelf) {
        const statusBtn = document.createElement("button");
        statusBtn.className = "btn-secondary";
        statusBtn.style.padding = "4px 12px";
        statusBtn.style.marginRight = "6px";
        statusBtn.textContent = suspended ? "활성화" : "정지";
        statusBtn.addEventListener("click", async () => {
          if (!suspended && !confirm(`${u.username} 계정을 정지하시겠습니까? 지금 로그인돼 있다면 즉시 로그아웃됩니다.`)) {
            return;
          }
          statusBtn.disabled = true;
          try {
            await api.post(`/api/admin/users/${u.id}/status`, {
              status: suspended ? "ACTIVE" : "SUSPENDED",
            });
            await load();
          } catch (err) {
            statusBtn.disabled = false;
            alert(err instanceof ApiError && err.status === 400 ? "처리할 수 없는 요청입니다." : "처리 중 오류가 발생했습니다.");
          }
        });
        actionCell.appendChild(statusBtn);

        const deleteBtn = document.createElement("button");
        deleteBtn.className = "btn-secondary";
        deleteBtn.style.padding = "4px 12px";
        deleteBtn.style.color = "var(--danger)";
        deleteBtn.textContent = "삭제";
        deleteBtn.addEventListener("click", async () => {
          if (
            !confirm(
              `${u.username} 계정을 삭제하시겠습니까?\n로그인·에이전트 연동이 모두 즉시 끊기며 되돌릴 수 없습니다(거래 이력 자체는 보존됩니다).`,
            )
          ) {
            return;
          }
          deleteBtn.disabled = true;
          try {
            await api.delete(`/api/admin/users/${u.id}`);
            await load();
          } catch {
            deleteBtn.disabled = false;
            alert("삭제 중 오류가 발생했습니다.");
          }
        });
        actionCell.appendChild(deleteBtn);
      }

      tbody.appendChild(tr);
    }

    scrollWrap.appendChild(table);
    tableCard.replaceChildren(scrollWrap);
  }

  await load();
}
